import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Divider,
  Grid,
  Chip,
  Button,
  Stack,
} from '@mui/material';
import {
  AccountCircle,
  Email,
  Badge,
  CheckCircle,
  Cancel,
  Login,
  Logout,
  Settings,
  Lock,
  Edit,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PageIndices } from '../common/i-doc/i-doc.registry.ts';
import { PageHint } from '@/components/common/PageHint';
import { useAuth } from '@/hooks/useAuth';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

export const ProfilePage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return (
      <DashboardLayout data-i-doc={PageIndices.PROFILE}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography>Користувача не знайдено</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  const infoItems = [
    {
      icon: <Email color="primary" />,
      label: 'Email',
      value: user.email,
    },
    {
      icon: user.isEmailVerified ? <CheckCircle color="success" /> : <Cancel color="error" />,
      label: 'Email підтверджено',
      value: user.isEmailVerified ? 'Так' : 'Ні',
    },
    {
      icon: <Login color="primary" />,
      label: 'Останній вхід',
      value: user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Немає даних',
    },
    {
      icon: <Badge color="primary" />,
      label: 'Статус опитування',
      value: user.isFirstSurveyCompleted ? 'Пройдено' : 'Не пройдено',
    },
  ];

  const handleNavigateToSettings = (tab: string) => {
    navigate('/settings', { state: { activeTab: tab } });
  };

  return (
    <DashboardLayout data-i-doc={PageIndices.PROFILE}>
      <PageHint iDoc={PageIndices.PROFILE} />
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 700 }}>
          Мій Профіль
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ textAlign: 'center', p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Avatar
                src={user.avatar || undefined}
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 2,
                  fontSize: '3rem',
                  bgcolor: 'primary.main',
                }}
              >
                {user.name ? user.name.charAt(0).toUpperCase() : <AccountCircle fontSize="large" />}
              </Avatar>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                {user.name || 'Користувач'}
              </Typography>
              <Typography color="text.secondary" gutterBottom>
                {user.email}
              </Typography>
              <Box sx={{ mt: 1, mb: 3, display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                {user.roles?.map((role) => (
                  <Chip key={role} label={role} size="small" color="primary" variant="outlined" />
                ))}
              </Box>

              <Box sx={{ mt: 'auto' }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={() => handleNavigateToSettings('profile')}
                  sx={{ mb: 1 }}
                >
                  Редагувати дані
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Lock />}
                  onClick={() => handleNavigateToSettings('access')}
                >
                  Змінити пароль
                </Button>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Детальна інформація
                  </Typography>
                  <Button 
                    size="small" 
                    startIcon={<Settings />} 
                    onClick={() => navigate('/settings')}
                  >
                    Всі налаштування
                  </Button>
                </Box>
                
                <Grid container spacing={3}>
                  {infoItems.map((item, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                        {item.icon}
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {item.label}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {item.value}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 4 }} />

                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Керування акаунтом
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Ви можете змінити свої персональні дані, налаштувати одиниці вимірювання або керувати підпискою у розділі налаштувань.
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Logout />}
                    onClick={logout}
                  >
                    Вийти з системи
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
};
